import { TitleCasePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { APPLICATION_ROLES } from '../../../app/constants/application-roles.constant';
import { LOCAL_STORAGE_KEYS } from '../../enums/local-storage.enum';
import { IUserProfile } from '../../models/login.model';
import { AuthService } from '../../services/auth/auth.service';
import { StorageService } from '../../services/storage/storage-service';
import { UserService } from '../../services/user/user.service';

@Component({
    selector: 'pims-user-profile-menu',
    imports: [TranslatePipe, MatExpansionModule, TitleCasePipe],
    templateUrl: './user-profile-menu.component.html',
    styleUrl: './user-profile-menu.component.scss',
})
export class UserProfileMenuComponent implements OnInit {
    private readonly authService = inject(AuthService);
    private readonly storageService = inject(StorageService);
    private readonly userService = inject(UserService);
    private readonly router = inject(Router);

    userRoles: string[] = [];
    userProfile: IUserProfile | null = null;
    activeRole: string | null = null;
    hasImageError: boolean = false;

    ngOnInit() {
        this.userRoles = this.storageService.getLocalItem<string[]>(LOCAL_STORAGE_KEYS.USER_ROLES) ?? [];
        this.filterUserRoles();
        this.userProfile = this.storageService.getLocalItem<IUserProfile>(LOCAL_STORAGE_KEYS.USER_PROFILE);
        this.activeRole = this.storageService.getLocalItem<string>(LOCAL_STORAGE_KEYS.ACTIVE_ROLE);
    }

    onImageError(): void {
        this.hasImageError = true;
    }

    get profileImageUrl(): string | null {
        const img = this.userProfile?.profileImage?.trim();
        if (this.hasImageError || !img) return null;
        return img.startsWith('data:') || img.startsWith('http') ? img : `data:image/png;base64,${img}`;
    }

    get username(): string {
        return this.userProfile?.preferred_username || '';
    }

    get displayProfile(): string {
        const profile = this.userProfile?.profile?.trim();
        if (!profile) return '';
        return profile
            .split(' ')
            .map((w) =>
                w.length > 1 && w === w.toUpperCase() ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
            )
            .join(' ');
    }

    get profileAndUsername(): string {
        const p = this.displayProfile;
        const u = this.username ? `(${this.username})` : '';
        return p && u ? `${p} ${u}` : p || u;
    }

    get displayActiveRole(): string {
        if (!this.activeRole) return '';
        return this.activeRole
            .split('-')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
    }

    filterUserRoles(): void {
        this.userRoles = APPLICATION_ROLES.filter((role: string) => this.userRoles.includes(role));
    }

    logout(): void {
        this.authService.logout();
    }

    switchRole(role: string): void {
        if (role === this.activeRole) return;
        this.userService.setActiveRole(role);
        this.storageService.setLocalItem(LOCAL_STORAGE_KEYS.ACTIVE_ROLE, role);
        this.activeRole = role;
        this.router.navigate(['/web/dashboard']);
    }
}
